import type { PermissionGroup } from "@/features/roles/types/roles";

import {
  PERMISSION_AREA_LABEL,
  PERMISSION_SECTIONS,
  PERMISSION_SHORT_LABEL,
  type Permission,
  type PermissionSection,
} from "../constants/permissions";

import { type Access, CONTACT_VISIBILITY } from "@/types/role";
import { capitalize } from "@/utils/string";

const ITEMS = PERMISSION_SECTIONS.flatMap((section) => section.permissions);

export const getPermissionLabel = (key: string) =>
  ITEMS.find((item) => item.key === key)?.label ?? key;

// The sections as the editor lists them: only the keys the backend has, and
// any it has that the app has no wording for yet, by their key, at the end.
export const getPermissionSections = (
  keys: Permission[],
): PermissionSection[] => {
  const unnamed = keys.filter((key) => !ITEMS.some((item) => item.key === key));
  const sections = PERMISSION_SECTIONS.map((section) => ({
    ...section,
    permissions: section.permissions.filter((item) => keys.includes(item.key)),
  })).filter((section) => section.permissions.length > 0);

  if (unnamed.length === 0) return sections;

  return [
    ...sections,
    {
      key: "other",
      label: "Other",
      description: "Newer abilities.",
      permissions: unnamed.map((key) => ({ key, label: key })),
    },
  ];
};

export const areaOf = (key: string) => key.split(".")[0];

export const getAreaLabel = (key: string) =>
  PERMISSION_AREA_LABEL[areaOf(key)] ?? areaOf(key);

// The verb a key goes by under its area's name: "view", "book and move".
export const getPermissionShortLabel = (key: Permission) =>
  PERMISSION_SHORT_LABEL[key] ?? key.split(".")[1];

// "View, book and move, delete", or "" when there's nothing.
export const listShortLabels = (keys: Permission[]) =>
  capitalize(keys.map(getPermissionShortLabel).join(", "));

// The editor's rows: one per area, in its catalog section. An area's first key
// describes the area.
export const getPermissionGroups = (keys: Permission[]): PermissionGroup[] =>
  getPermissionSections(keys).map((section) => ({
    key: section.key,
    label: section.label,
    areas: [
      ...new Set(section.permissions.map((item) => areaOf(item.key))),
    ].map((area) => {
      const items = section.permissions.filter(
        (item) => areaOf(item.key) === area,
      );

      return {
        key: area,
        label: getAreaLabel(items[0].key),
        hint: items[0].description,
        permissions: items.map((item) => item.key),
      };
    }),
  }));

// Which contacts an access can see. Two equal keys see the same contacts.
export const getContactScopeKey = (access?: Access | null): string =>
  [
    access?.contactVisibility ?? CONTACT_VISIBILITY.ALL,
    ...[...(access?.teamIds ?? [])].sort(),
  ].join(":");
